// Génère la carte de participation à partager (image PNG 1080 x 1350,
// format portrait adapté à Instagram, Facebook et LinkedIn).
// Tout est dessiné dans le navigateur : rien n'est envoyé à un serveur.

import andarLogo from "../assets/andar-logo.png";
import badgeLogo from "../assets/polyartrottons-badge.png";

// Site de l'événement affiché sur la carte (le lien du formulaire figure
// dans le message qui accompagne le partage).
const SITE_EVENEMENT = "www.polyarthrite-andar.org";

const LARGEUR = 1080;
const HAUTEUR = 1350;

const COULEURS = {
  magenta: "#c1115e",
  bleu: "#079cda",
  teal: "#33b6a3",
  jaune: "#dddd00",
  orange: "#f39202",
  texte: "#2b2b2b",
  gris: "#6b6b6b",
  rose: "#fbeaf1",
};

// Participe passé de chaque type de performance, conjugué ensuite en
// "J'ai ..." (seul) ou "À 15, nous avons ..." (en groupe).
// "parPersonne" : quantité comptée pour chaque membre du groupe (le montant
// est multiplié par le nombre de personnes pour ces types).
const PHRASES = {
  marche: { participe: "parcouru", unite: (q) => "km", parPersonne: true },
  fitness: { participe: "enchaîné", unite: (q) => (q > 1 ? "répétitions" : "répétition"), parPersonne: true },
  dictee: { participe: "écrit", unite: (q) => (q > 1 ? "dictées" : "dictée") },
  qcm: { participe: "répondu à", unite: (q) => "QCM" },
  enigme: { participe: "résolu", unite: (q) => (q > 1 ? "énigmes" : "énigme") },
  dessin: { participe: "réalisé", unite: (q) => (q > 1 ? "dessins" : "dessin") },
  autre: { participe: "participé à", unite: (q) => (q > 1 ? "activités collectives" : "activité collective"), parPersonne: true },
};

const nombre = (n) => Number(n).toLocaleString("fr-FR", { maximumFractionDigits: 1 });
const euros = (n) =>
  Number(n).toLocaleString("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: n % 1 ? 2 : 0 });

function chargerImage(src) {
  return new Promise((ok, ko) => {
    const img = new Image();
    img.onload = () => ok(img);
    img.onerror = ko;
    img.src = src;
  });
}

async function chargerPolices() {
  if (!document.fonts || !document.fonts.load) return;
  await Promise.all([
    document.fonts.load('700 200px "Oswald"'),
    document.fonts.load('500 60px "Oswald"'),
    document.fonts.load('600 44px "Oswald"'),
    document.fonts.load('400 40px "Mulish"'),
    document.fonts.load('700 40px "Mulish"'),
    document.fonts.load('40px "Homemade Apple"'),
  ]).catch(() => {});
}

function rectangleArrondi(ctx, x, y, l, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + l, y, x + l, y + h, r);
  ctx.arcTo(x + l, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + l, y, r);
  ctx.closePath();
}

// Réduit la taille de police jusqu'à ce que le texte tienne dans la largeur.
function ajusterPolice(ctx, texte, largeurMax, taille, style) {
  let t = taille;
  ctx.font = style(t);
  while (ctx.measureText(texte).width > largeurMax && t > 20) {
    t -= 4;
    ctx.font = style(t);
  }
  return t;
}

function bandeCouleurs(ctx, y, h) {
  const couleurs = [COULEURS.bleu, COULEURS.magenta, COULEURS.teal, COULEURS.jaune, COULEURS.orange];
  couleurs.forEach((c, i) => {
    ctx.fillStyle = c;
    ctx.fillRect((LARGEUR / 5) * i, y, LARGEUR / 5 + 1, h);
  });
}

export async function genererCarteParticipation({ entrees, total, rang, nombrePersonnes = 1, categorie, structure }) {
  await chargerPolices();
  const [logo, badge] = await Promise.all([chargerImage(andarLogo), chargerImage(badgeLogo)]);

  const canvas = document.createElement("canvas");
  canvas.width = LARGEUR;
  canvas.height = HAUTEUR;
  const ctx = canvas.getContext("2d");
  ctx.textBaseline = "alphabetic";

  const groupe = Number(nombrePersonnes) > 1;
  const triees = [...entrees].sort((a, b) => b.montant - a.montant);
  const principale = triees[0];
  const phrase = PHRASES[principale.type] || PHRASES.autre;
  const avecEtiquette = structure && (categorie === "club" || categorie === "entreprise" || categorie === "hopital");
  const LIMITE_CONTENU = HAUTEUR - 290; // le bloc « Et vous ? » commence en dessous

  // Dessine toute la carte avec une taille donnée pour le grand chiffre et
  // renvoie la position du bas du contenu. On réduit ensuite cette taille
  // jusqu'à ce que tout tienne sans chevauchement.
  function dessiner(tailleChiffre, avecRang, compact) {
    ctx.clearRect(0, 0, LARGEUR, HAUTEUR);

    // Fond et formes décoratives
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, LARGEUR, HAUTEUR);
    ctx.fillStyle = COULEURS.rose;
    ctx.beginPath();
    ctx.arc(LARGEUR + 60, 700, 330, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 0.14;
    ctx.fillStyle = COULEURS.teal;
    ctx.beginPath();
    ctx.arc(-80, 1120, 260, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = COULEURS.jaune;
    ctx.beginPath();
    ctx.arc(1000, 1180, 120, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;
    bandeCouleurs(ctx, 0, 22);

    // En-tête : logo ANDAR + badge Polyar'Trottons
    const hLogo = compact ? 90 : 110;
    ctx.drawImage(logo, 80, compact ? 70 : 80, (logo.width / logo.height) * hLogo, hLogo);
    const lBadge = compact ? 170 : 210;
    ctx.drawImage(badge, LARGEUR - 80 - lBadge, 55, lBadge, (badge.height / badge.width) * lBadge);

    // Accroche manuscrite
    ctx.fillStyle = COULEURS.magenta;
    ctx.font = '44px "Homemade Apple", cursive';
    ctx.fillText("Chaque pas compte !", 80, compact ? 300 : 345);

    let y = compact ? 328 : 375;

    // Club, entreprise (Défis Pro) ou service hospitalier / de soins
    if (avecEtiquette) {
      const prefixes = { entreprise: "Défis Pro · ", hopital: "Challenge services hospitaliers · ", club: "Club · " };
      const etiquette = (prefixes[categorie] || "") + structure;
      ajusterPolice(ctx, etiquette, LARGEUR - 240, 34, (t) => `700 ${t}px "Mulish", sans-serif`);
      const lEtiquette = ctx.measureText(etiquette).width + 48;
      ctx.fillStyle = categorie === "entreprise" ? COULEURS.orange : categorie === "hopital" ? COULEURS.bleu : COULEURS.teal;
      rectangleArrondi(ctx, 80, y, lEtiquette, 58, 29);
      ctx.fill();
      ctx.fillStyle = "#ffffff";
      ctx.fillText(etiquette, 104, y + 40);
      y += 58;
    }

    // Verbe : « J'ai parcouru » ou « À 15, nous avons parcouru »
    y += 80;
    const verbe = groupe ? `À ${nombre(nombrePersonnes)}, nous avons ${phrase.participe}` : `J'ai ${phrase.participe}`;
    ctx.fillStyle = COULEURS.texte;
    ajusterPolice(ctx, verbe, LARGEUR - 160, 56, (t) => `700 ${t}px "Mulish", sans-serif`);
    ctx.fillText(verbe, 80, y);

    // Grand chiffre + unité
    const valeur = nombre(principale.quantite);
    const taille = ajusterPolice(ctx, valeur, 620, tailleChiffre, (t) => `700 ${t}px "Oswald", sans-serif`);
    ctx.fillStyle = COULEURS.magenta;
    ctx.font = `700 ${taille}px "Oswald", sans-serif`;
    y += taille * 0.95;
    ctx.fillText(valeur, 72, y);
    const largeurValeur = ctx.measureText(valeur).width;
    const uniteTexte = phrase.unite(principale.quantite);
    const tailleUnite = ajusterPolice(ctx, uniteTexte, LARGEUR - 80 - (92 + largeurValeur), Math.min(80, taille * 0.4),
      (t) => `500 ${t}px "Oswald", sans-serif`);
    ctx.font = `500 ${tailleUnite}px "Oswald", sans-serif`;
    ctx.fillText(uniteTexte, 92 + largeurValeur, y);

    if (groupe && phrase.parPersonne) {
      y += 52;
      ctx.fillStyle = COULEURS.gris;
      ctx.font = '700 36px "Mulish", sans-serif';
      ctx.fillText("par personne", 80, y);
    }

    // Autres performances (2 au maximum)
    const autres = triees.slice(1, 3).map((e) => {
      const p = PHRASES[e.type] || PHRASES.autre;
      return `+ ${nombre(e.quantite)} ${p.unite(e.quantite)}`;
    });
    if (triees.length > 3) {
      const n = triees.length - 3;
      autres.push(n > 1 ? `+ ${n} autres défis` : "+ 1 autre défi");
    }
    if (autres.length) {
      y += 58;
      ctx.fillStyle = COULEURS.gris;
      ajusterPolice(ctx, autres.join("   "), LARGEUR - 160, 42, (t) => `700 ${t}px "Mulish", sans-serif`);
      ctx.fillText(autres.join("   "), 80, y);
    }

    // Message
    y += 72;
    ctx.fillStyle = COULEURS.texte;
    // « pour la recherche sur la polyarthrite rhumatoïde / et les actions de l'ANDAR. »
    const ligne1 = "pour la recherche sur la polyarthrite rhumatoïde";
    ajusterPolice(ctx, ligne1, LARGEUR - 160, compact ? 40 : 44, (t) => `400 ${t}px "Mulish", sans-serif`);
    ctx.fillText(ligne1, 80, y);
    y += compact ? 54 : 60;
    ctx.font = `700 ${compact ? 40 : 44}px "Mulish", sans-serif`;
    ctx.fillText("et les actions de l'ANDAR.", 80, y);

    // Pastille montant
    y += 42;
    const texteMontant = `soit ${euros(total)} pour la recherche`;
    ctx.font = '600 44px "Oswald", sans-serif';
    const lPastille = ctx.measureText(texteMontant).width + 80;
    ctx.fillStyle = COULEURS.magenta;
    rectangleArrondi(ctx, 80, y, lPastille, 88, 44);
    ctx.fill();
    ctx.fillStyle = "#ffffff";
    ctx.fillText(texteMontant, 120, y + 60);
    y += 88;

    if (avecRang && rang) {
      const ordinal = rang === 1 ? "la 1re" : `la ${nombre(rang)}ᵉ`;
      y += 60;
      ctx.fillStyle = COULEURS.gris;
      ctx.font = '400 34px "Mulish", sans-serif';
      ctx.fillText(
        groupe ? `Nous sommes ${ordinal} équipe à relever le défi.` : `Je suis ${ordinal} personne à relever le défi.`,
        80,
        y
      );
    }

    // Appel à l'action + site de l'événement
    ctx.fillStyle = COULEURS.texte;
    ctx.font = '700 58px "Oswald", sans-serif';
    ctx.fillText("ET VOUS ?", 80, HAUTEUR - 205);
    ctx.fillStyle = COULEURS.magenta;
    ctx.font = '700 38px "Mulish", sans-serif';
    ctx.fillText("Rejoignez Polyar'Trottons avec l'ANDAR sur", 80, HAUTEUR - 150);
    const lien = SITE_EVENEMENT;
    ajusterPolice(ctx, lien, LARGEUR - 160, 44, (t) => `700 ${t}px "Mulish", sans-serif`);
    ctx.fillStyle = COULEURS.bleu;
    ctx.fillText(lien, 80, HAUTEUR - 88);

    bandeCouleurs(ctx, HAUTEUR - 22, 22);
    return y;
  }

  // On cherche la plus grande taille de chiffre qui laisse tout tenir,
  // d'abord avec la ligne du rang, puis sans si la place manque.
  // Ordre d'essai : mise en page aérée, puis compacte, puis sans le rang.
  // Pour chaque essai, on accepte le chiffre à partir de 150 px.
  const essais = [[true, false], [true, true], [false, true]];
  let placee = false;
  for (const [avecRang, compact] of essais) {
    for (let t = 230; t >= 150; t -= 20) {
      if (dessiner(t, avecRang, compact) <= LIMITE_CONTENU) {
        placee = true;
        break;
      }
    }
    if (placee) break;
  }
  if (!placee) {
    for (let t = 140; t >= 90; t -= 10) {
      if (dessiner(t, false, true) <= LIMITE_CONTENU) break;
    }
  }

  return new Promise((ok) => canvas.toBlob((blob) => ok(blob), "image/png"));
}
